/**
 * Pure helpers for the drive-time pins (2026-10-09): number formatting, the
 * one-line address Google gets, and the route-map embed URL.
 */
import { formatCustomerAddress } from "../formatters/customerAddress";

export const METERS_PER_MILE = 1609.344;

/** The four address fields and nothing else — the shape `DriveAddressInput` takes. */
export const toAddressInput = (like) => ({
  streetAddress: String(like?.streetAddress || ""),
  city: String(like?.city || ""),
  state: String(like?.state || ""),
  zipCode: String(like?.zipCode || ""),
});

export const addressText = (like) => formatCustomerAddress(toAddressInput(like));

export const hasAddress = (like) => addressText(like).length > 0;

/** Two places are the same pin when their addresses read the same. */
export const samePlace = (a, b) => {
  if (!a || !b) return false;
  const x = addressText(a.address).trim().toLowerCase();
  const y = addressText(b.address).trim().toLowerCase();
  return !!x && x === y;
};

/** "14.2 mi" under ten miles, "23 mi" above — the tenth stops mattering. */
export const formatMiles = (meters) => {
  if (meters == null || !Number.isFinite(meters)) return "";
  const mi = meters / METERS_PER_MILE;
  return `${mi >= 10 ? Math.round(mi) : mi.toFixed(1)} mi`;
};

/** "23 min", "1 hr", "1 hr 5 min". */
export const formatMinutes = (seconds) => {
  if (seconds == null || !Number.isFinite(seconds)) return "";
  const min = Math.round(seconds / 60);
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const r = min % 60;
  return r ? `${h} hr ${r} min` : `${h} hr`;
};

/** One leg as a phrase: "23 min · 14.2 mi", or "no route" when Google had none. */
export const legSummary = (leg) => {
  if (!leg) return "";
  if (!leg.ok) return "no route";
  return `${formatMinutes(leg.durationSec)} · ${formatMiles(leg.distanceMeters)}`;
};

/** Sum of the present legs' seconds, or null if any present leg has no route. */
export const totalSeconds = (legs) => {
  const present = (legs || []).filter(Boolean);
  if (present.length === 0) return null;
  if (present.some((l) => !l.ok)) return null;
  return present.reduce((s, l) => s + (l.durationSec || 0), 0);
};

/**
 * The classic Maps embed draws a route from `saddr` to `daddr`, and `daddr`
 * takes a `+to:` waypoint — so start → target → end is one URL. Keyless, the
 * same endpoint the calendar popup already embeds for a single pin.
 */
export const buildRouteEmbedUrl = ({ start, target, end }) => {
  const q = (like) => encodeURIComponent(addressText(like));
  if (!hasAddress(target)) return null;
  if (hasAddress(start) && hasAddress(end)) {
    return `https://maps.google.com/maps?saddr=${q(start)}&daddr=${q(target)}+to:${q(end)}&output=embed`;
  }
  if (hasAddress(start)) {
    return `https://maps.google.com/maps?saddr=${q(start)}&daddr=${q(target)}&output=embed`;
  }
  if (hasAddress(end)) {
    return `https://maps.google.com/maps?saddr=${q(target)}&daddr=${q(end)}&output=embed`;
  }
  return null;
};
